import type { ReactNode } from 'react';

export function Panel({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <section className="border-b border-[color:var(--line)] px-4 py-4 last:border-b-0">
      <h2 className="mb-4 text-sm font-semibold">{title}</h2>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

export function RangeControl({
  label,
  value,
  min,
  max,
  step = 1,
  suffix,
  onChange
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  suffix?: string;
  onChange: (value: number) => void;
}): JSX.Element {
  return (
    <label className="block">
      <div className="mb-2 flex items-center justify-between gap-3">
        <span className="control-label">{label}</span>
        <span className="text-xs font-medium text-[color:var(--muted)]">
          {value}
          {suffix}
        </span>
      </div>
      <input
        className="h-2 w-full accent-[color:var(--accent)]"
        type="range"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </label>
  );
}

export function Toggle({
  label,
  checked,
  onChange
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="flex w-full items-center justify-between gap-3 rounded-md border border-[color:var(--line)] bg-[color:var(--panel)] px-3 py-2 text-sm font-medium"
    >
      <span>{label}</span>
      <span className={`relative h-5 w-9 rounded-full transition ${checked ? 'bg-[color:var(--accent)]' : 'bg-[color:var(--line)]'}`}>
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-[color:var(--panel)] transition ${checked ? 'left-4' : 'left-0.5'}`} />
      </span>
    </button>
  );
}

export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}): JSX.Element {
  return (
    <div>
      <div className="control-label mb-2">{label}</div>
      <div className="grid grid-cols-2 gap-1 rounded-md border border-[color:var(--line)] bg-[color:var(--panel-strong)] p-1">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={`rounded px-2 py-2 text-xs font-semibold transition ${
              value === option.value ? 'bg-[color:var(--panel)] text-[color:var(--text)] shadow-sm' : 'text-[color:var(--muted)] hover:text-[color:var(--text)]'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function ColorControl({
  label,
  value,
  onChange
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}): JSX.Element {
  return (
    <label className="block">
      <span className="control-label mb-2 block">{label}</span>
      <span className="flex items-center gap-2">
        <input className="h-10 w-12 rounded-md border border-[color:var(--line)] bg-transparent p-1" type="color" value={value} onChange={(event) => onChange(event.target.value)} />
        <input className="input-field min-w-0 flex-1" value={value} onChange={(event) => onChange(event.target.value)} />
      </span>
    </label>
  );
}
